/**
 * useInfraPolling — FEAT-MONITOR-001 (Infrastructure monitor)
 * Batches fleet-servers + datastores + queues on one timer. Datastores/queues are cheap and
 * refresh every tick; servers ping remote agents so also every tick (they have their own
 * timeouts server-side). Keeps last-good on failure (stale-dim, never blank).
 */
import { useCallback, useEffect, useState } from "react";
import { fetchWrapper } from "../../helpers";

// Admin-monitor is its OWN service (e.g. https://adminmonitor.xnotify.com); it aggregates the
// per-box metrics agents. Falls back to the general API URL only for local dev.
const BASE = process.env.REACT_APP_MONITOR_API_URL || process.env.REACT_APP_API_URL;

export default function useInfraPolling({ intervalMs = 5000 }) {
  const [servers, setServers] = useState(null);
  const [datastores, setDatastores] = useState(null);
  const [queues, setQueues] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [lastOkAt, setLastOkAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const get = useCallback((path) => fetchWrapper.get(`${BASE}${path}`), []);

  const tick = useCallback(async () => {
    setBusy(true);
    let anyOk = false;
    try {
      const [sv, ds, qu] = await Promise.all([
        get("/admin/monitor/infra/servers").then((r) => (r?.success !== false ? r.data : null)).catch(() => null),
        get("/admin/monitor/infra/datastores").then((r) => (r?.success !== false ? r.data : null)).catch(() => null),
        get("/admin/monitor/infra/queues").then((r) => (r?.success !== false ? r.data : null)).catch(() => null),
      ]);
      if (sv) { setServers(sv); anyOk = true; }
      if (ds) { setDatastores(ds); anyOk = true; }
      if (qu) { setQueues(qu); anyOk = true; }
      if (anyOk) { setStale(false); setLastOkAt(new Date().toISOString()); setError(null); }
      else { setStale(true); setError("Couldn't reach infrastructure API"); }
    } catch (e) {
      setStale(true); setError(e?.message || "Couldn't reach infrastructure API");
    } finally { setBusy(false); }
  }, [get]);

  useEffect(() => {
    tick();
    if (!intervalMs) return undefined;
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [tick, intervalMs]);

  return { servers, datastores, queues, error, stale, lastOkAt, busy, refresh: tick };
}
